import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Users, Plus, Edit2, Trash2, Loader2, X, Check, IndianRupee, Upload, Download, FileSpreadsheet, AlertCircle, BookOpen, ShieldCheck, Wallet } from "lucide-react";

import CohortImportDialog from "@/components/admin/cohort-import-dialog";
import CohortCreditsDialog from "@/components/admin/cohort-credits-dialog";
import { TEMPLATE_CSV } from "@/lib/cohort-import";

const BASE = import.meta.env.BASE_URL;

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

function fmtINR(n: number | null | undefined): string {
  if (!n || n <= 0) return "—";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)} K`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "favcy_cohort_investors_template.csv";
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminInvestors() {
  const [investors, setInvestors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);
  const [researchInvestor, setResearchInvestor] = useState<any | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);

  const load = async () => {
    try {
      const r = await fetch(`${BASE}api/admin/investors`, { headers: authHeaders() });
      if (r.ok) setInvestors(await r.json());
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const totalCapital = investors.reduce((s, i) => s + (i.totalInvested || 0), 0);

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5 -mx-2 md:mx-0">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2"><Users className="w-7 h-7 text-[#FF5C00]" /> Investors</h1>
          <p className="text-white/55 text-sm mt-1">Manage every investor on the platform — set how much they've invested in each portfolio company, or import a batch of investors into a cohort.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setCreditsOpen(true)} data-testid="button-open-credits" className="bg-white/[0.06] hover:bg-white/[0.1] text-white/85 border border-white/15 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2"><Wallet className="w-4 h-4" /> Payments &amp; credits</button>
          <button onClick={downloadTemplate} className="bg-white/[0.06] hover:bg-white/[0.1] text-white/85 border border-white/15 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2"><Download className="w-4 h-4" /> Template</button>
          <button onClick={() => setUploadOpen(true)} className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2"><Upload className="w-4 h-4" /> Import to cohort</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="Total investors" value={investors.length.toString()} />
        <Stat label="With allocations" value={investors.filter(i => (i.allocations?.length || 0) > 0).length.toString()} />
        <Stat label="Capital deployed" value={fmtINR(totalCapital)} />
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
      ) : investors.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-12 text-center">
          <Users className="w-10 h-10 text-white/20 mx-auto mb-2" />
          <p className="text-white/55 text-sm">No investors yet. Use Import to cohort above to add your first batch.</p>
        </div>
      ) : (
        <div className="border border-white/[0.08] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-white/50 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Investor</th>
                <th className="text-left px-4 py-3 font-medium">Firm / Type</th>
                <th className="text-left px-4 py-3 font-medium">Deals</th>
                <th className="text-right px-4 py-3 font-medium">Total invested</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {investors.map(inv => (
                <tr key={inv.id} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <p className="text-white font-medium">{inv.name}</p>
                    <p className="text-white/45 text-xs">{inv.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-white/85">{inv.firm || "—"}</p>
                    <p className="text-white/40 text-xs uppercase tracking-wide font-mono">{inv.type}</p>
                  </td>
                  <td className="px-4 py-3 text-white/70">{inv.allocations?.length || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[#FF5C00] font-mono font-medium">{fmtINR(inv.totalInvested)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <Link href={`/admin/email?tab=compose&email=${encodeURIComponent(inv.email || "")}`} data-testid={`link-prepare-email-${inv.id}`} className="text-white/70 hover:text-white text-xs px-2.5 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 inline-flex items-center gap-1.5">Prepare email</Link>
                      <Link href={`/admin/email?tab=people&email=${encodeURIComponent(inv.email || "")}`} data-testid={`link-email-history-${inv.id}`} className="text-white/70 hover:text-white text-xs px-2.5 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 inline-flex items-center gap-1.5">View history</Link>
                      <ExportPortfolioButton investorId={inv.id} investorName={inv.name} holdingCount={inv.allocations?.length || 0} />
                      <button onClick={() => setResearchInvestor(inv)} className="text-white/70 hover:text-white text-xs px-2.5 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 inline-flex items-center gap-1.5">
                        <BookOpen className="w-3 h-3" /> Research access
                      </button>
                      <button onClick={() => setEditing(inv)} className="text-white/70 hover:text-white text-xs px-2.5 py-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 inline-flex items-center gap-1.5">
                        <Edit2 className="w-3 h-3" /> Manage allocations
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <AllocationsDialog investor={editing} onClose={() => setEditing(null)} onChange={load} />}
      {researchInvestor && <ResearchAccessDialog investor={researchInvestor} onClose={() => setResearchInvestor(null)} />}
      {creditsOpen && <CohortCreditsDialog onClose={() => setCreditsOpen(false)} onChange={load} />}
      {uploadOpen && <CohortImportDialog onClose={() => setUploadOpen(false)} onComplete={load} />}
    </div>
  );
}

type ResearchAccessItem = {
  pimId: number;
  companyName: string;
  publishedVersionId: number;
  version: number | null;
  publishedAt: string | null;
  sector: string | null;
  accessible: boolean;
  accessSources: Array<{ kind: "cohort"; cohortId: number; cohortName: string } | { kind: "direct"; grantId: number; reason: string }>;
  directGrantId: number | null;
};

function ResearchAccessDialog({ investor, onClose }: { investor: any; onClose: () => void }) {
  const [items, setItems] = useState<ResearchAccessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPimId, setSelectedPimId] = useState("");
  const [reason, setReason] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notice, setNotice] = useState("");

  const readError = async (response: Response) => {
    const body = await response.json().catch(() => null);
    return body?.error?.message || body?.message || body?.error || `Request failed (${response.status})`;
  };
  const loadAccess = async () => {
    setLoading(true); setError("");
    try {
      const response = await fetch(`${BASE}api/admin/investors/${investor.id}/research-access`, { headers: authHeaders() });
      if (!response.ok) throw new Error(await readError(response));
      const body = await response.json();
      setItems(body.data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load research access.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void loadAccess(); }, [investor.id]);

  const grant = async () => {
    const pimId = Number(selectedPimId);
    if (!Number.isInteger(pimId) || reason.trim().length < 10) {
      setError("Select a published PIM and enter a reason of at least 10 characters."); return;
    }
    setBusyId(pimId); setError(""); setNotice("");
    try {
      const response = await fetch(`${BASE}api/admin/investors/${investor.id}/research-access`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ pimId, reason }),
      });
      if (!response.ok) throw new Error(await readError(response));
      setNotice("Direct access granted to the exact currently published version.");
      setSelectedPimId(""); setReason("");
      await loadAccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not grant research access.");
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (item: ResearchAccessItem) => {
    const revokeReason = prompt("Why is this direct grant being revoked? (minimum 10 characters)");
    if (!revokeReason || revokeReason.trim().length < 10) return;
    setBusyId(item.pimId); setError(""); setNotice("");
    try {
      const response = await fetch(`${BASE}api/admin/investors/${investor.id}/research-access/${item.directGrantId}`, {
        method: "DELETE", headers: authHeaders(), body: JSON.stringify({ reason: revokeReason }),
      });
      if (!response.ok) throw new Error(await readError(response));
      const body = await response.json();
      setNotice(body.data.explanation);
      await loadAccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke direct access.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-card text-foreground border border-white/15 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-card border-b border-white/10 px-6 py-4 flex items-start justify-between z-10">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#FF5C00]" /> Research Access</h2>
            <p className="text-muted-foreground text-sm mt-1">{investor.name} · published PIMs only</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-3">
            <p className="text-sm font-semibold">Grant an individual published PIM</p>
            <p className="text-xs text-muted-foreground">Only the configured PC or Bharat account can grant or revoke. A direct grant is pinned to this immutable published version and does not add the investor to a cohort or campaign.</p>
            <div className="grid md:grid-cols-[1fr_1.4fr_auto] gap-2">
              <select value={selectedPimId} onChange={e => setSelectedPimId(e.target.value)} className="bg-background border border-white/15 rounded-lg px-3 py-2 text-sm">
                <option value="">Select published PIM…</option>
                {items.filter(item => !item.directGrantId).map(item => <option key={item.pimId} value={item.pimId}>{item.companyName} · v{item.version ?? "—"}</option>)}
              </select>
              <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for individual access…" className="bg-background border border-white/15 rounded-lg px-3 py-2 text-sm" />
              <button onClick={grant} disabled={busyId !== null} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center justify-center gap-2">
                {busyId !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Grant
              </button>
            </div>
          </div>
          {error && <div className="rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-sm text-red-200">{error}</div>}
          {notice && <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-200">{notice}</div>}
          {loading ? <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading effective access…</div> : (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Effective access and source</h3>
              {items.map(item => (
                <div key={item.pimId} className="border border-white/10 rounded-xl p-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium">{item.companyName} <span className="text-xs text-muted-foreground">v{item.version ?? "—"}</span></p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.accessSources.length === 0
                        ? "No access"
                        : item.accessSources.map(source => source.kind === "direct" ? "Direct individual grant" : `Cohort: ${source.cohortName}`).join(" · ")}
                    </p>
                  </div>
                  {item.directGrantId && (
                    <button onClick={() => revoke(item)} disabled={busyId !== null} className="shrink-0 border border-red-400/30 text-red-300 hover:bg-red-400/10 rounded-lg px-3 py-1.5 text-xs">
                      {busyId === item.pimId ? "Revoking…" : "Revoke direct"}
                    </button>
                  )}
                </div>
              ))}
              {items.length === 0 && <p className="text-sm text-muted-foreground py-5 text-center">No live published PIMs are available.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Per-row "Export portfolio" button. Fetches the CSV with the admin token in
// headers (a plain <a href> won't work because the endpoint requires
// x-admin-token), turns it into a blob, and triggers a browser download named
// like the server's Content-Disposition. Disabled when the investor has zero
// holdings — exporting an empty file is misleading and the agent earlier
// learned the hard way (Prayush) what a blank file feels like to receive.
function ExportPortfolioButton({ investorId, investorName, holdingCount }: { investorId: number; investorName: string; holdingCount: number }) {
  const [busy, setBusy] = useState(false);
  const empty = holdingCount === 0;
  const onClick = async () => {
    if (empty || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}api/admin/investors/${investorId}/portfolio.csv`, { headers: { "x-admin-token": localStorage.getItem("admin_token") || "" } });
      if (!r.ok) { alert(`Export failed (${r.status}). Check the network tab.`); return; }
      const blob = await r.blob();
      const disp = r.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m?.[1] || `portfolio_${investorName.replace(/[^a-z0-9]+/gi, "_")}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { alert("Network error while exporting"); }
    setBusy(false);
  };
  return (
    <button
      onClick={onClick}
      disabled={empty || busy}
      title={empty ? "No holdings to export yet — add allocations first" : `Download ${investorName}'s portfolio as CSV`}
      className={`text-xs px-2.5 py-1.5 rounded-lg border inline-flex items-center gap-1.5 ${empty ? "text-white/30 bg-white/[0.02] border-white/[0.06] cursor-not-allowed" : "text-white/85 bg-white/[0.05] hover:bg-white/[0.1] border-white/15"}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />}
      {busy ? "Exporting…" : "Export portfolio"}
    </button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-3.5">
      <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{label}</p>
      <p className="text-2xl font-bold text-white mt-1">{value}</p>
    </div>
  );
}

function AllocationsDialog({ investor, onClose, onChange }: { investor: any; onClose: () => void; onChange: () => void }) {
  const [allocs, setAllocs] = useState<any[]>(investor.allocations || []);
  const [companies, setCompanies] = useState<any[]>([]);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newCompanyId, setNewCompanyId] = useState<string>("");
  const [newAmount, setNewAmount] = useState<string>("");
  const [newDate, setNewDate] = useState<string>("");
  const [newAccess, setNewAccess] = useState<string>("summary");
  const [adding, setAdding] = useState(false);

  // Bulk-distribute helpers: enter a single total, pick which rows to spread it
  // across (default = all), click "Distribute equally" → amounts auto-fill in
  // local state. Then "Save all" persists every selected row in one click.
  const [bulkTotalStr, setBulkTotalStr] = useState<string>("");
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set());
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    fetch(`${BASE}api/companies`).then(r => r.ok ? r.json() : []).then(setCompanies).catch(() => {});
  }, []);

  const total = allocs.reduce((s, a) => s + (Number(a.amountInvested) || 0), 0);

  const isSelected = (id: number) => !excludedIds.has(id);
  const selectedCount = allocs.filter(a => isSelected(a.id)).length;
  const toggleRow = (id: number) => setExcludedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setExcludedIds(new Set());
  const selectNone = () => setExcludedIds(new Set(allocs.map(a => a.id)));

  const distributeEqually = () => {
    const total = Math.round(Number(bulkTotalStr) || 0);
    if (total <= 0 || selectedCount === 0) return;
    const per = Math.floor(total / selectedCount);
    const remainder = total - per * selectedCount;
    let firstSelectedSeen = false;
    setAllocs(prev => prev.map(a => {
      if (!isSelected(a.id)) return a;
      const amt = per + (firstSelectedSeen ? 0 : remainder);
      firstSelectedSeen = true;
      return { ...a, amountInvested: amt };
    }));
  };

  // Persists a draft (id < 0) by POSTing to create the row, then PATCHing the
  // amountInvested/entryDate (the POST endpoint's Zod schema only accepts
  // investorId/companyId/accessLevel and silently strips other fields, so a
  // single POST would lose the user's amount/date). Returns the new row's
  // server-assigned id, or null on failure.
  const persistDraft = async (a: any): Promise<{ id: number; row: any } | null> => {
    const post = await fetch(`${BASE}api/investor-access`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({
        investorId: investor.id,
        companyId: a.companyId,
        accessLevel: a.accessLevel || "summary",
      }),
    });
    if (!post.ok) return null;
    const created = await post.json();
    const hasAmount = !(a.amountInvested === "" || a.amountInvested === null || a.amountInvested === undefined);
    const hasDate = !!a.entryDate;
    if (hasAmount || hasDate) {
      const patch = await fetch(`${BASE}api/investor-access/${created.id}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({
          amountInvested: hasAmount ? Number(a.amountInvested) : null,
          entryDate: hasDate ? a.entryDate : null,
          accessLevel: a.accessLevel || "summary",
        }),
      });
      if (patch.ok) {
        const updated = await patch.json();
        return { id: updated.id, row: { ...updated, companyName: a.companyName } };
      }
      // POST landed but PATCH failed — return the bare created row so the user
      // at least sees the row was created (they can retry the amount edit).
      return { id: created.id, row: { ...created, companyName: a.companyName } };
    }
    return { id: created.id, row: { ...created, companyName: a.companyName } };
  };

  // Save all = persist every row currently in local state (whether checked or
  // not). The checkbox is only for the Distribute step. Saving the value the
  // user sees on screen is the least surprising behavior. Drafts (id < 0) get
  // POSTed (then PATCHed for amount/date); existing rows get PATCHed. After
  // save, drafts are swapped to their real IDs by MERGING into current state
  // (not replacing) so the user's mid-flight edits to other fields aren't
  // clobbered.
  const saveAll = async () => {
    if (allocs.length === 0) return;
    setSavingAll(true);
    let okCount = 0; let failCount = 0;
    const swaps: Array<{ tempId: number; newId: number }> = [];
    for (const a of allocs) {
      try {
        if (a.id < 0 || a._draft) {
          const result = await persistDraft(a);
          if (result) { okCount++; swaps.push({ tempId: a.id, newId: result.id }); }
          else failCount++;
        } else {
          const r = await fetch(`${BASE}api/investor-access/${a.id}`, {
            method: "PATCH", headers: authHeaders(),
            body: JSON.stringify({
              amountInvested: a.amountInvested === "" || a.amountInvested === null || a.amountInvested === undefined ? null : Number(a.amountInvested),
              entryDate: a.entryDate || null,
              accessLevel: a.accessLevel,
            }),
          });
          if (r.ok) okCount++; else failCount++;
        }
      } catch { failCount++; }
    }
    // Merge new IDs into current state — preserves any field edits the user
    // made on those rows during the save round-trip.
    if (swaps.length > 0) {
      setAllocs(prev => prev.map(a => {
        const swap = swaps.find(s => s.tempId === a.id);
        return swap ? { ...a, id: swap.newId, _draft: false } : a;
      }));
    }
    setSavingAll(false);
    if (okCount > 0) onChange();
    if (failCount > 0) alert(`Saved ${okCount}/${allocs.length}. ${failCount} failed — check the network tab and retry.`);
  };

  const saveAlloc = async (a: any) => {
    setSavingId(a.id);
    try {
      if (a.id < 0 || a._draft) {
        const result = await persistDraft(a);
        if (result) {
          setAllocs(prev => prev.map(x => x.id === a.id ? { ...x, id: result.id, _draft: false } : x));
          onChange();
        }
      } else {
        const r = await fetch(`${BASE}api/investor-access/${a.id}`, {
          method: "PATCH", headers: authHeaders(),
          body: JSON.stringify({
            amountInvested: a.amountInvested === "" || a.amountInvested === null ? null : Number(a.amountInvested),
            entryDate: a.entryDate || null,
            accessLevel: a.accessLevel,
          }),
        });
        if (r.ok) { onChange(); }
      }
    } catch {}
    setSavingId(null);
  };

  const removeAlloc = async (id: number) => {
    // Drafts (negative IDs) haven't been persisted yet — just discard locally,
    // no API call, no confirm. Real allocations require a confirm because
    // deleting a saved allocation is destructive (loses amountInvested history).
    if (id < 0) {
      setAllocs(allocs.filter(a => a.id !== id));
      setExcludedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      return;
    }
    if (!confirm("Remove this allocation?")) return;
    try {
      const r = await fetch(`${BASE}api/investor-access/${id}`, { method: "DELETE", headers: authHeaders() });
      if (r.ok || r.status === 204) { setAllocs(allocs.filter(a => a.id !== id)); onChange(); }
    } catch {}
  };

  // Adds a "draft" row for a company the investor has no allocation in yet.
  // Drafts use a negative ID so the rest of the UI machinery (row rendering,
  // distribute math, per-row checkboxes, Save all) treats them like existing
  // rows. The save loop converts negative IDs into POSTs instead of PATCHes.
  // This is what unblocks the bulk-distribute flow for investors that previously
  // had zero allocations (the panel used to be hidden behind allocs.length >= 2).
  const addDraft = (companyId: number, companyName: string) => {
    const tempId = -(Date.now() + Math.floor(Math.random() * 1000));
    setAllocs(prev => [...prev, {
      id: tempId,
      _draft: true,
      investorId: investor.id,
      companyId,
      companyName,
      amountInvested: null,
      entryDate: null,
      accessLevel: "summary",
      scope: "full",
      status: "active",
    }]);
  };

  // Companies the investor has no allocation in yet — these are the candidates
  // for the "Add company to spread across" picker. Drafts already in `allocs`
  // are also excluded so we don't offer the same company twice.
  const allocCompanyIds = new Set(allocs.map(a => a.companyId));
  const unallocatedCompanies = companies.filter(c => !allocCompanyIds.has(c.id));

  const addAlloc = async () => {
    if (!newCompanyId) return;
    setAdding(true);
    try {
      const r = await fetch(`${BASE}api/investor-access`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          investorId: investor.id,
          companyId: parseInt(newCompanyId, 10),
          accessLevel: newAccess,
          scope: "full",
          status: "active",
          amountInvested: newAmount ? Number(newAmount) : null,
          entryDate: newDate || null,
        }),
      });
      if (r.ok) {
        const created = await r.json();
        const company = companies.find(c => c.id === parseInt(newCompanyId, 10));
        setAllocs([...allocs, { ...created, companyName: company?.name || "" }]);
        setNewCompanyId(""); setNewAmount(""); setNewDate(""); setAddOpen(false);
        onChange();
      }
    } catch {}
    setAdding(false);
  };

  const updateLocal = (id: number, patch: any) => setAllocs(allocs.map(a => a.id === id ? { ...a, ...patch } : a));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-white/[0.1] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/[0.08] p-5 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">{investor.name}</h2>
            <p className="text-white/50 text-xs mt-0.5">{investor.email} · Total invested <span className="text-[#FF5C00] font-mono">{fmtINR(total)}</span></p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-3">
          {/* Bulk-distribute panel — ALWAYS visible (previously gated on
              allocs.length >= 2, which hid it for every investor with 0 or 1
              deals). The picker below lets the admin add unallocated companies
              as draft rows so the same flow works for a brand-new investor. */}
          <div className="bg-[#FF5C00]/[0.06] border border-[#FF5C00]/30 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-white text-sm font-medium">Distribute a total across companies</p>
              <p className="text-white/50 text-[11px] font-mono">{selectedCount}/{allocs.length} selected</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2">
              <Field label="Total amount (₹)">
                <div className="relative">
                  <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
                  <input
                    type="number"
                    value={bulkTotalStr}
                    onChange={e => setBulkTotalStr(e.target.value)}
                    placeholder="e.g. 5000000"
                    className="w-full bg-black/40 border border-white/10 rounded-lg pl-7 pr-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none"
                  />
                </div>
                {Number(bulkTotalStr) > 0 ? <p className="text-white/40 text-[10px] mt-0.5">{fmtINR(Number(bulkTotalStr))}</p> : null}
              </Field>
              <div className="flex items-end gap-1">
                <button onClick={selectAll} disabled={allocs.length === 0} className="bg-white/[0.06] hover:bg-white/[0.1] disabled:opacity-30 text-white/70 text-[11px] px-2 py-1.5 rounded-lg">All</button>
                <button onClick={selectNone} disabled={allocs.length === 0} className="bg-white/[0.06] hover:bg-white/[0.1] disabled:opacity-30 text-white/70 text-[11px] px-2 py-1.5 rounded-lg">None</button>
              </div>
              <div className="flex items-end">
                <button
                  onClick={distributeEqually}
                  disabled={!(Number(bulkTotalStr) > 0) || selectedCount === 0}
                  className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5" /> Distribute equally
                </button>
              </div>
            </div>
            {/* Add-company picker — visible whenever there are companies the
                investor isn't allocated to yet. Selecting one drops a draft
                row into the list below; admin can add several before clicking
                Distribute / Save all. */}
            {unallocatedCompanies.length > 0 && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/50 font-mono block mb-1">
                  {allocs.length === 0 ? "Pick companies to allocate to" : "Add another company to the spread"}
                </label>
                <select
                  value=""
                  onChange={e => {
                    const cid = parseInt(e.target.value, 10);
                    if (!cid) return;
                    const c = unallocatedCompanies.find(x => x.id === cid);
                    if (c) addDraft(cid, c.name);
                    e.currentTarget.value = "";
                  }}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none"
                  data-testid="select-add-company-to-spread"
                >
                  <option value="">{allocs.length === 0 ? "Pick the first company…" : "Add another company…"}</option>
                  {unallocatedCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {allocs.length === 0 ? (
              <p className="text-white/45 text-[11px]">Pick one or more companies above, enter a total, then click Distribute equally.</p>
            ) : selectedCount > 0 && Number(bulkTotalStr) > 0 ? (
              <p className="text-white/55 text-[11px]">
                Splits to <span className="text-[#FF5C00] font-mono">{fmtINR(Math.floor(Number(bulkTotalStr) / selectedCount))}</span> per company
                {Number(bulkTotalStr) % selectedCount !== 0 ? <span className="text-white/40"> (remainder of ₹{Number(bulkTotalStr) % selectedCount} added to the first row)</span> : null}.
                Amounts auto-fill below — review and click <span className="text-white/80">Save all</span> to commit.
              </p>
            ) : (
              <p className="text-white/45 text-[11px]">Enter a total, untick any company you want to skip, then click Distribute.</p>
            )}
            <div className="flex justify-end pt-1 border-t border-white/[0.06]">
              <button
                onClick={saveAll}
                disabled={savingAll || allocs.length === 0}
                className="bg-white/10 hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                data-testid="button-save-all-allocations"
              >
                {savingAll ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving {allocs.length}…</> : <><Check className="w-3 h-3" /> Save all ({allocs.length})</>}
              </button>
            </div>
          </div>

          {allocs.length === 0 && !addOpen && unallocatedCompanies.length === 0 && (
            <p className="text-white/45 text-sm text-center py-8">No companies exist yet — create one in Companies first, then come back to allocate.</p>
          )}

          {allocs.map(a => (
            <div key={a.id} className={`bg-white/[0.04] border rounded-xl p-4 space-y-3 transition-colors ${isSelected(a.id) ? "border-white/[0.08]" : "border-white/[0.04] opacity-60"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {allocs.length >= 2 && (
                    <input
                      type="checkbox"
                      checked={isSelected(a.id)}
                      onChange={() => toggleRow(a.id)}
                      className="mt-1 w-4 h-4 accent-[#FF5C00] cursor-pointer"
                      title={isSelected(a.id) ? "Exclude from bulk distribute" : "Include in bulk distribute"}
                    />
                  )}
                  <div>
                    <p className="text-white font-medium">{a.companyName}</p>
                    <p className={`text-[10px] uppercase tracking-wider font-mono ${a.id < 0 || a._draft ? "text-[#FF5C00]" : "text-white/40"}`}>
                      {a.id < 0 || a._draft ? "Draft — not saved yet" : `Allocation #${a.id}`}
                    </p>
                  </div>
                </div>
                <button onClick={() => removeAlloc(a.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Field label="Amount invested (₹)">
                  <div className="relative">
                    <IndianRupee className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
                    <input type="number" value={a.amountInvested ?? ""} onChange={e => updateLocal(a.id, { amountInvested: e.target.value })}
                      placeholder="0" className="w-full bg-black/40 border border-white/10 rounded-lg pl-7 pr-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none" />
                  </div>
                  {a.amountInvested ? <p className="text-white/40 text-[10px] mt-0.5">{fmtINR(Number(a.amountInvested))}</p> : null}
                </Field>
                <Field label="Entry date">
                  <input type="date" value={a.entryDate ? String(a.entryDate).slice(0, 10) : ""} onChange={e => updateLocal(a.id, { entryDate: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none" />
                </Field>
                <Field label="Access level">
                  <select value={a.accessLevel} onChange={e => updateLocal(a.id, { accessLevel: e.target.value })}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none">
                    <option value="summary">Summary</option>
                    <option value="full">Full</option>
                  </select>
                </Field>
              </div>
              <button onClick={() => saveAlloc(a)} disabled={savingId === a.id}
                className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                {savingId === a.id ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving</> : <><Check className="w-3 h-3" /> Save changes</>}
              </button>
            </div>
          ))}

          {addOpen ? (
            <div className="bg-white/[0.04] border border-[#FF5C00]/30 rounded-xl p-4 space-y-3">
              <p className="text-white text-sm font-medium">New allocation</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Field label="Company">
                  <select value={newCompanyId} onChange={e => setNewCompanyId(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none">
                    <option value="">Pick a company…</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Amount invested (₹)">
                  <input type="number" value={newAmount} onChange={e => setNewAmount(e.target.value)} placeholder="2500000"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none" />
                </Field>
                <Field label="Entry date">
                  <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none" />
                </Field>
                <Field label="Access level">
                  <select value={newAccess} onChange={e => setNewAccess(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white focus:border-[#FF5C00]/50 outline-none">
                    <option value="summary">Summary</option>
                    <option value="full">Full</option>
                  </select>
                </Field>
              </div>
              <div className="flex gap-2">
                <button onClick={addAlloc} disabled={!newCompanyId || adding} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5">
                  {adding ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding</> : <><Check className="w-3.5 h-3.5" /> Add</>}
                </button>
                <button onClick={() => setAddOpen(false)} className="text-white/60 hover:text-white text-sm px-3 py-1.5">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddOpen(true)} className="w-full bg-white/[0.03] hover:bg-white/[0.06] border border-dashed border-white/15 rounded-xl py-3 text-white/65 text-sm flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Add allocation
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-white/50 font-mono block mb-1">{label}</label>
      {children}
    </div>
  );
}
